import { supabase }             from '../supabase.js';
import { COLORS }               from '../lib/theme.js';
import { openDrillPanel }       from '../components/drill-panel.js';
import { openEditStudentModal } from '../components/edit-student.js';
import { canChange }            from '../lib/access.js';
import ExcelJS                  from 'exceljs';

const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
);
const pctColor = p => p >= 90 ? COLORS.green : p >= 60 ? COLORS.amber : COLORS.red;

// ── Icons ─────────────────────────────────────────────────────────────────
const iconBank = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
  <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>`;

const iconUif = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`;

const iconId = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
  <rect x="2" y="5" width="20" height="14" rx="2"/>
  <circle cx="8" cy="12" r="2"/>
  <path d="M14 9h4M14 12h4M14 15h2"/></svg>`;

const iconTax = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
  <polyline points="14 2 14 8 20 8"/>
  <line x1="9" y1="13" x2="15" y2="13"/>
  <line x1="9" y1="17" x2="15" y2="17"/></svg>`;

const iconContract = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
  <polyline points="14 2 14 8 20 8"/>
  <polyline points="9 15 11 17 15 13"/></svg>`;

const iconQual = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
  <circle cx="12" cy="8" r="6"/>
  <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>`;

const iconChev = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
  style="width:14px;height:14px"><path d="M15 18l-6-6 6-6"/></svg>`;

// ── Compliance field definitions ──────────────────────────────────────────
const COMP_FIELDS = [
  { key: 'banking', dataField: 'bank_account_number', verField: 'bank_verified',
    label: 'Banking Details',   missingLabel: 'no banking', icon: iconBank },
  { key: 'uif',     dataField: 'uif_ref_number',       verField: 'uif_ref_verified',
    label: 'UIF Reference No.', missingLabel: 'no UIF ref', icon: iconUif },
  { key: 'id',      dataField: 'national_id',           verField: 'national_id_verified',
    label: 'ID Numbers',        missingLabel: 'no ID',      icon: iconId },
  { key: 'tax',      dataField: 'tax_ref_number',        verField: 'tax_ref_verified',
    label: 'Tax Reference No.',      missingLabel: 'no tax ref',      icon: iconTax },
  { key: 'contract', dataField: 'contract_file_url',    verField: 'contract_verified',
    label: 'Contracts',              missingLabel: 'no contract',     icon: iconContract },
  { key: 'qual',     dataField: 'highest_qualification', verField: 'qualification_verified',
    label: 'Highest Qualification',  missingLabel: 'no qualification', icon: iconQual },
];

// Fields to fetch for every student
const STU_SEL = `student_uuid, first_name, surname, national_id, national_id_verified, status,
  bank_account_number, bank_name, branch_code, bank_verified,
  uif_ref_number, uif_ref_verified,
  tax_ref_number, tax_ref_verified,
  contract_file_url, contract_verified,
  highest_qualification, qualification_verified,
  payroll_remarks`;

// ── Module state ──────────────────────────────────────────────────────────
let _muniList     = [];
let _muniVenueIds = new Map();
let _venueMap     = new Map();
let _facClasses   = [];
let _statusFilter = ''; // '' | 'active' | 'withdrawn'

// ── Styles ────────────────────────────────────────────────────────────────
const STYLES = `<style>
  .comp-kpi-card { transition:transform .15s,box-shadow .15s; }
  .comp-kpi-card:hover { transform:translateY(-3px); box-shadow:0 6px 24px rgba(0,0,0,.10); }
  .comp-row      { background:var(--surface); border:1px solid var(--border); border-radius:10px;
                   padding:14px 16px; cursor:pointer; margin-bottom:8px;
                   transition:transform .12s,box-shadow .12s; }
  .comp-row:hover { transform:translateY(-1px); box-shadow:0 3px 12px rgba(0,0,0,.08); }
  .comp-back     { display:inline-flex; align-items:center; gap:5px; margin-bottom:14px;
                   font-size:13px; color:var(--text-muted); background:none; border:none;
                   padding:4px 0; cursor:pointer; }
  .comp-back:hover { color:var(--text); }
  .comp-edit-btn:hover { opacity:.85; }
  .comp-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); gap:16px; margin-bottom:8px; }
</style>`;

/* ═══════════════════════════════════════════════════ ENTRY ═══ */
export async function renderCompliance() {
  document.getElementById('view-compliance').innerHTML = `
    ${STYLES}
    <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px">
      <div>
        <h1>Compliance</h1>
        <p>Document &amp; data compliance. Click any card to explore by municipality &rarr; site &rarr; class &rarr; learner.</p>
      </div>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <select id="comp-status-filter"
          style="padding:7px 10px;border:1px solid var(--border);border-radius:8px;
                 font-size:13px;background:var(--surface);color:var(--text);outline:none;cursor:pointer">
          <option value="">All Learners</option>
          <option value="active">Active</option>
          <option value="withdrawn">Withdrawn (Dropouts)</option>
          <option value="graduated">Graduated</option>
          <option value="suspended">Suspended</option>
          <option value="transferred">Transferred</option>
          <option value="inactive">Inactive</option>
        </select>
        <button id="comp-download-btn" class="btn-secondary" disabled
          style="display:inline-flex;align-items:center;gap:7px;padding:8px 16px;font-size:13px;
                 font-weight:500;border:1px solid var(--border);border-radius:8px;
                 background:var(--surface);color:var(--text);cursor:pointer;white-space:nowrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Download Excel
        </button>
      </div>
    </div>
    <div id="comp-body"><div style="color:var(--text-muted);padding:16px">Loading&hellip;</div></div>`;

  document.getElementById('comp-status-filter').addEventListener('change', e => {
    _statusFilter = e.target.value;
    if (window.__access?.seeAll === true) renderAdminStats();
    else renderFacilitatorStats();
  });

  if (window.__access?.seeAll === true) await renderAdmin();
  else await renderFacilitator();
}

/* ═══════════════════════════════════════════════════ ADMIN ═══ */
async function fetchAllEnrollments() {
  const PAGE = 1000;
  let from = 0, all = [];
  while (true) {
    const { data, error } = await supabase
      .from('student_class_enrollments')
      .select(`class_id, students( ${STU_SEL} )`)
      .eq('status', 'enrolled')
      .range(from, from + PAGE - 1);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

function applyStatusFilter(students) {
  if (!_statusFilter) return students;
  return students.filter(s => s.status === _statusFilter);
}
const STATUS_LABELS = {
  active:      'Active Learners',
  withdrawn:   'Withdrawn Learners',
  graduated:   'Graduated Learners',
  suspended:   'Suspended Learners',
  transferred: 'Transferred Learners',
  inactive:    'Inactive Learners',
};
function filterLabel() {
  return STATUS_LABELS[_statusFilter] || 'Total Learners';
}
function filterSuffix() {
  return _statusFilter ? `_${_statusFilter.charAt(0).toUpperCase() + _statusFilter.slice(1)}` : '';
}

async function renderAdmin() {
  const body = document.getElementById('comp-body');

  const [clsRes, enrData] = await Promise.all([
    supabase.from('classes').select(`
      class_id, class_name,
      training_venues(
        training_venue_id, training_venue_name,
        towns( town_id, municipalities( municipality_id, municipality_name ) )
      )
    `).or('is_test.is.null,is_test.eq.false').order('class_name'),
    fetchAllEnrollments(),
  ]);

  if (clsRes.error) { body.innerHTML = `<p style="color:#DC2626">${clsRes.error.message}</p>`; return; }

  // Build class -> students map
  const studentsByClass = new Map();
  for (const e of enrData) {
    if (!e.students) continue;
    if (!studentsByClass.has(e.class_id)) studentsByClass.set(e.class_id, []);
    studentsByClass.get(e.class_id).push(e.students);
  }

  // Build hierarchy cache
  _muniList     = [];
  _muniVenueIds = new Map();
  _venueMap     = new Map();
  const muniSeen = new Map();

  for (const cls of clsRes.data || []) {
    const venue = cls.training_venues;
    const muni  = venue?.towns?.municipalities;
    if (!venue || !muni) continue;

    if (!muniSeen.has(muni.municipality_id)) {
      muniSeen.set(muni.municipality_id, muni.municipality_name);
      _muniVenueIds.set(muni.municipality_id, new Set());
    }
    _muniVenueIds.get(muni.municipality_id).add(venue.training_venue_id);

    if (!_venueMap.has(venue.training_venue_id)) {
      _venueMap.set(venue.training_venue_id, {
        id: venue.training_venue_id, name: venue.training_venue_name,
        muni_id: muni.municipality_id, classes: [],
      });
    }
    _venueMap.get(venue.training_venue_id).classes.push({
      id: cls.class_id, name: cls.class_name,
      students: (studentsByClass.get(cls.class_id) || [])
                  .sort((a, b) => (a.surname || '').localeCompare(b.surname || '')),
    });
  }
  _muniList = [...muniSeen.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const dlBtn = document.getElementById('comp-download-btn');
  dlBtn.disabled = false;
  dlBtn.addEventListener('click', async () => {
    const muniId = await pickMuniModal();
    if (muniId === undefined) return;
    downloadExcel(muniId).catch(console.error);
  });

  renderAdminStats();
}

function renderAdminStats() {
  const body = document.getElementById('comp-body');

  const seen = new Set();
  const allStudents = [];
  for (const v of _venueMap.values())
    for (const c of v.classes)
      for (const s of applyStatusFilter(c.students))
        if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const context = _statusFilter
    ? `${STATUS_LABELS[_statusFilter] || 'Filtered learners'} across all classes and programmes`
    : 'All learners across all classes and programmes';

  body.innerHTML =
    totalBanner(allStudents.length, context) +
    `<div class="comp-grid">
      ${COMP_FIELDS.map(fld => kpiWidget(`comp-widget-${fld.key}`, fld, fieldStats(allStudents, fld))).join('')}
    </div>`;

  COMP_FIELDS.forEach(fld =>
    document.getElementById(`comp-widget-${fld.key}`)
      ?.addEventListener('click', () => panelMunicipalities(fld))
  );

  document.querySelectorAll('.comp-field-dl').forEach(btn =>
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const fld = COMP_FIELDS.find(f => f.key === btn.dataset.key);
      if (fld) downloadFieldAdmin(fld).catch(console.error);
    })
  );
}

/* ═══════════════════════════════ ADMIN DRILL — Municipalities ═══ */
function panelMunicipalities(fld) {
  const cards = _muniList.map(m => {
    const all = [];
    for (const vid of (_muniVenueIds.get(m.id) || [])) {
      const v = _venueMap.get(vid);
      if (!v) continue;
      for (const c of v.classes) all.push(...applyStatusFilter(c.students));
    }
    const { total, verified, unverified, noData: nd } = fieldStats(all, fld);
    return rowCard(`cm-${m.id}`, m.name, total, verified, unverified, nd, `exp-m-${m.id}`);
  }).join('') || noData();

  openDrillPanel({ eyebrow: fld.label, title: 'By Municipality', html: cards });
  _muniList.forEach(m => {
    document.getElementById(`cm-${m.id}`)
      ?.addEventListener('click', () => panelVenues(m.id, m.name, fld));
    document.getElementById(`exp-m-${m.id}`)
      ?.addEventListener('click', e => { e.stopPropagation(); downloadFieldMuni(fld, m.id, m.name).catch(console.error); });
  });
}

/* ═══════════════════════════════ ADMIN DRILL — Venues ═══ */
function panelVenues(muniId, muniName, fld) {
  const venues = [...(_muniVenueIds.get(muniId) || [])]
    .map(id => _venueMap.get(id)).filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  const cards = venues.map(v => {
    const all = v.classes.flatMap(c => applyStatusFilter(c.students));
    const { total, verified, unverified, noData: nd } = fieldStats(all, fld);
    return rowCard(`cv-${v.id}`, v.name, total, verified, unverified, nd, `exp-v-${v.id}`);
  }).join('') || noData('No sites found for this municipality.');

  setPanelContent(muniName, 'By Training Venue / Site', back('All Municipalities') + cards);
  document.getElementById('comp-back').addEventListener('click', () => panelMunicipalities(fld));
  venues.forEach(v => {
    document.getElementById(`cv-${v.id}`)
      ?.addEventListener('click', () => panelClasses(v, muniId, muniName, fld));
    document.getElementById(`exp-v-${v.id}`)
      ?.addEventListener('click', e => { e.stopPropagation(); downloadFieldVenue(fld, v, muniName).catch(console.error); });
  });
}

/* ═══════════════════════════════ ADMIN DRILL — Classes ═══ */
function panelClasses(venue, muniId, muniName, fld) {
  if (venue.classes.length === 1) {
    panelLearners(venue.classes[0], venue, muniId, muniName, fld, false);
    return;
  }
  const classes = [...venue.classes].sort((a, b) => a.name.localeCompare(b.name));
  const cards = classes.map(c => {
    const { total, verified, unverified, noData: nd } = fieldStats(applyStatusFilter(c.students), fld);
    return rowCard(`cc-${c.id}`, c.name, total, verified, unverified, nd, `exp-c-${c.id}`);
  }).join('') || noData('No classes found at this site.');

  setPanelContent(venue.name, 'By Class', back(esc(muniName)) + cards);
  document.getElementById('comp-back').addEventListener('click', () => panelVenues(muniId, muniName, fld));
  classes.forEach(c => {
    document.getElementById(`cc-${c.id}`)
      ?.addEventListener('click', () => panelLearners(c, venue, muniId, muniName, fld, true));
    document.getElementById(`exp-c-${c.id}`)
      ?.addEventListener('click', e => { e.stopPropagation(); downloadFieldClass(fld, c, venue.name).catch(console.error); });
  });
}

/* ═══════════════════════════════ ADMIN DRILL — Learners ═══ */
function panelLearners(cls, venue, muniId, muniName, fld, cameFromClasses) {
  const backFn = cameFromClasses
    ? () => panelClasses(venue, muniId, muniName, fld)
    : () => panelVenues(muniId, muniName, fld);

  const students = applyStatusFilter(cls.students);
  setPanelContent(
    cls.name,
    `${students.length} Learner${students.length !== 1 ? 's' : ''}`,
    back(cameFromClasses ? esc(venue.name) : esc(muniName)) + learnerHtml(students, fld)
  );
  document.getElementById('comp-back').addEventListener('click', backFn);
  wireLearners(students, fld);
}

/* ═══════════════════════════════ FACILITATOR VIEW ═══ */
async function renderFacilitator() {
  const body = document.getElementById('comp-body');

  const { data: { user } } = await supabase.auth.getUser();
  const { data: ur } = await supabase
    .from('user_roles').select('facilitator_id').eq('user_id', user.id).single();
  if (!ur?.facilitator_id) {
    body.innerHTML = noData('No facilitator profile is linked to your account. Contact an administrator.');
    return;
  }

  const { data: classes, error: clsErr } = await supabase
    .from('classes')
    .select('class_id, class_name, training_venues(training_venue_name)')
    .eq('facilitator_id', ur.facilitator_id)
    .or('is_test.is.null,is_test.eq.false')
    .order('class_name');
  if (clsErr) { body.innerHTML = noData(clsErr.message); return; }
  if (!classes?.length) { body.innerHTML = noData('No classes are currently assigned to you.'); return; }

  const classIds = classes.map(c => c.class_id);
  const PAGE = 1000;
  let enr = [], from = 0;
  while (true) {
    const { data, error } = await supabase
      .from('student_class_enrollments')
      .select(`class_id, students( ${STU_SEL} )`)
      .in('class_id', classIds)
      .eq('status', 'enrolled')
      .range(from, from + PAGE - 1);
    if (error) break;
    enr = enr.concat(data || []);
    if (!data || data.length < PAGE) break;
    from += PAGE;
  }

  const byClass = new Map(classes.map(c => [c.class_id, []]));
  for (const e of enr || []) { if (e.students) byClass.get(e.class_id)?.push(e.students); }

  _facClasses = classes.map(c => ({
    class_id:   c.class_id,
    class_name: c.class_name,
    venue_name: c.training_venues?.training_venue_name || '',
    students:   (byClass.get(c.class_id) || [])
                  .sort((a, b) => (a.surname || '').localeCompare(b.surname || '')),
  }));

  const dlBtn = document.getElementById('comp-download-btn');
  dlBtn.disabled = false;
  dlBtn.addEventListener('click', () => downloadExcelFacilitator().catch(console.error));

  renderFacilitatorStats();
}

function renderFacilitatorStats() {
  const body = document.getElementById('comp-body');

  const seen = new Set();
  const allStudents = [];
  for (const fc of _facClasses)
    for (const s of applyStatusFilter(fc.students))
      if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const classLabel = `${_facClasses.length} class${_facClasses.length !== 1 ? 'es' : ''} assigned to you`;

  body.innerHTML =
    totalBanner(allStudents.length, classLabel) +
    `<div class="comp-grid">
      ${COMP_FIELDS.map(fld => kpiWidget(`comp-fac-widget-${fld.key}`, fld, fieldStats(allStudents, fld))).join('')}
    </div>`;

  COMP_FIELDS.forEach(fld =>
    document.getElementById(`comp-fac-widget-${fld.key}`)
      ?.addEventListener('click', () => facPanelClasses(fld))
  );

  document.querySelectorAll('.comp-field-dl').forEach(btn =>
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const fld = COMP_FIELDS.find(f => f.key === btn.dataset.key);
      if (fld) downloadFieldFacilitator(fld).catch(console.error);
    })
  );
}

/* ═══════════════════════════════ FAC DRILL — Classes ═══ */
function facPanelClasses(fld) {
  const cards = _facClasses.map(c => {
    const { total, verified, unverified, noData: nd } = fieldStats(applyStatusFilter(c.students), fld);
    return rowCard(`fc-${c.class_id}`, c.class_name, total, verified, unverified, nd, `exp-fc-${c.class_id}`);
  }).join('') || noData();

  openDrillPanel({ eyebrow: fld.label, title: 'My Classes', html: cards });
  _facClasses.forEach(c => {
    document.getElementById(`fc-${c.class_id}`)
      ?.addEventListener('click', () => facPanelLearners(c, fld));
    document.getElementById(`exp-fc-${c.class_id}`)
      ?.addEventListener('click', e => {
        e.stopPropagation();
        downloadFieldClass(fld, { id: c.class_id, name: c.class_name, students: c.students }, c.venue_name).catch(console.error);
      });
  });
}

/* ═══════════════════════════════ FAC DRILL — Learners ═══ */
function facPanelLearners(cls, fld) {
  const students = applyStatusFilter(cls.students);
  setPanelContent(
    cls.class_name,
    `${students.length} Learner${students.length !== 1 ? 's' : ''}`,
    back('My Classes') + learnerHtml(students, fld)
  );
  document.getElementById('comp-back').addEventListener('click', () => facPanelClasses(fld));
  wireLearners(students, fld);
}

/* ═══════════════════════════════ SHARED HELPERS ═══ */
function fieldStats(students, fld) {
  const hasVal     = s => !!String(s[fld.dataField] ?? '').trim();
  const total      = students.length;
  const verified   = students.filter(s =>  s[fld.verField]).length;
  const unverified = students.filter(s => !s[fld.verField] &&  hasVal(s)).length;
  const noData     = students.filter(s => !s[fld.verField] && !hasVal(s)).length;
  return { total, verified, unverified, noData };
}

function totalBanner(total, context) {
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;
                background:var(--surface);border:1px solid var(--border);border-radius:12px;
                padding:14px 20px;margin-bottom:16px">
      <div style="display:flex;align-items:baseline;gap:10px">
        <span style="font-size:28px;font-weight:700;color:var(--text);line-height:1">${total.toLocaleString()}</span>
        <span style="font-size:13px;font-weight:600;color:var(--text)">${filterLabel()}</span>
      </div>
      <span style="font-size:12px;color:var(--text-muted)">${context}</span>
    </div>`;
}

function kpiWidget(widgetId, fld, stats) {
  const { total, verified, unverified, noData: nd } = stats;
  const vPct = total ? Math.round((verified   / total) * 100) : 0;
  const uPct = total ? Math.round((unverified / total) * 100) : 0;
  const nPct = total ? Math.round((nd         / total) * 100) : 0;
  return `
    <div class="kpi-card comp-kpi-card" id="${esc(widgetId)}" style="cursor:pointer">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px">
        <div>
          <div class="kpi-label">${esc(fld.label)}</div>
          <div class="kpi-value">${vPct}%</div>
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
            <div class="kpi-sub">verified &middot; Click to explore &rarr;</div>
            <button class="comp-field-dl" data-key="${esc(fld.key)}"
              style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;
                     font-size:11px;font-weight:500;border:1px solid var(--border);border-radius:6px;
                     background:var(--surface);color:var(--text-muted);cursor:pointer;white-space:nowrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round" style="width:11px;height:11px">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export
            </button>
          </div>
        </div>
        <div class="kpi-icon-circle" style="background:${pctColor(vPct)}">${fld.icon}</div>
      </div>
      <div class="progress-bar" style="height:10px;margin-bottom:12px;display:flex;overflow:hidden;gap:1px">
        <div style="width:${vPct}%;background:${COLORS.green};height:100%;transition:width .3s"></div>
        <div style="width:${uPct}%;background:${COLORS.amber};height:100%;transition:width .3s"></div>
        <div style="width:${nPct}%;background:${COLORS.red};height:100%;transition:width .3s"></div>
      </div>
      <div style="display:flex;gap:${fld.key === 'qual' || fld.key === 'uif' ? '8px' : '16px'};${fld.key === 'qual' || fld.key === 'uif' ? 'flex-direction:column;flex-shrink:0;' : 'flex-wrap:wrap;'}">
        <div style="display:flex;align-items:center;gap:6px">
          <span style="width:8px;height:8px;border-radius:50%;background:${COLORS.green};flex-shrink:0"></span>
          <span style="font-size:12px;color:var(--text)"><strong>${verified.toLocaleString()}</strong>
            <span style="color:var(--text-muted)"> verified</span></span>
        </div>
        <div style="display:flex;align-items:center;gap:6px">
          <span style="width:8px;height:8px;border-radius:50%;background:${COLORS.amber};flex-shrink:0"></span>
          <span style="font-size:12px;color:var(--text)"><strong>${unverified.toLocaleString()}</strong>
            <span style="color:var(--text-muted)"> unverified</span></span>
        </div>
        <div style="display:flex;align-items:center;gap:6px">
          <span style="width:8px;height:8px;border-radius:50%;background:${COLORS.red};flex-shrink:0"></span>
          <span style="font-size:12px;color:var(--text)"><strong>${nd.toLocaleString()}</strong>
            <span style="color:var(--text-muted)"> ${esc(fld.missingLabel)}</span></span>
        </div>
      </div>
    </div>`;
}

function setPanelContent(eyebrow, title, html) {
  document.getElementById('drill-eyebrow').textContent = eyebrow;
  document.getElementById('drill-title').textContent   = title;
  document.getElementById('drill-body').innerHTML      = html;
}

function back(label) {
  return `<button class="comp-back" id="comp-back">${iconChev}${label}</button>`;
}

function rowCard(id, name, total, verified, unverified, nd, exportId) {
  const vPct = total ? Math.round((verified   / total) * 100) : 0;
  const uPct = total ? Math.round((unverified / total) * 100) : 0;
  const nPct = total ? Math.round((nd         / total) * 100) : 0;
  const expBtn = exportId ? `<button id="${esc(exportId)}" class="comp-row-exp"
      style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;font-size:11px;
             font-weight:500;border:1px solid var(--border);border-radius:5px;
             background:var(--surface);color:var(--text-muted);cursor:pointer;white-space:nowrap">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
        stroke-linecap="round" stroke-linejoin="round" style="width:10px;height:10px">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>Export</button>` : '';
  return `
    <div class="comp-row" id="${esc(id)}">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px">
        <span style="font-size:13px;font-weight:600;color:var(--text);line-height:1.3;flex:1;padding-right:8px">${esc(name)}</span>
        <div style="display:flex;align-items:center;gap:8px;flex-shrink:0">${expBtn}<span style="font-size:12px;color:var(--text-muted);white-space:nowrap">${total} learners</span></div>
      </div>
      <div class="progress-bar" style="margin-bottom:6px;display:flex;overflow:hidden;gap:1px">
        <div style="width:${vPct}%;background:${COLORS.green};height:100%;transition:width .3s"></div>
        <div style="width:${uPct}%;background:${COLORS.amber};height:100%;transition:width .3s"></div>
        <div style="width:${nPct}%;background:${COLORS.red};height:100%;transition:width .3s"></div>
      </div>
      <div style="display:flex;gap:14px;font-size:12px;flex-wrap:wrap">
        <span style="color:${COLORS.green};font-weight:600">&#10003; ${verified} verified</span>
        <span style="color:${COLORS.amber};font-weight:600">&#9651; ${unverified} unverified</span>
        <span style="color:${COLORS.red};font-weight:600">&#10005; ${nd} missing</span>
      </div>
    </div>`;
}

function learnerHtml(learners, fld) {
  return `
    <div style="margin-bottom:10px">
      <input id="comp-search" type="text" placeholder="Search name or ID&hellip;"
        style="width:100%;box-sizing:border-box;padding:8px 12px;border:1px solid var(--border);
               border-radius:8px;font-size:13px;background:var(--surface);color:var(--text);outline:none">
      <div id="comp-count" style="margin-top:6px;font-size:12px;color:var(--text-muted)">
        ${learners.length} learner${learners.length !== 1 ? 's' : ''}
      </div>
    </div>
    <div id="comp-rows">${learnerRows(learners, fld)}</div>`;
}

function learnerRows(list, fld) {
  if (!list.length) return `<div style="text-align:center;padding:32px 0;color:var(--text-muted);font-size:13px">No learners found.</div>`;
  return list.map(l => {
    const isVerified = !!l[fld.verField];
    const hasVal     = !!String(l[fld.dataField] ?? '').trim();
    const badgeClass = isVerified ? 'badge-green' : hasVal ? 'badge-orange' : 'badge-red';
    const badgeText  = isVerified ? 'verified &#10003;' : hasVal ? 'unverified' : esc(fld.missingLabel);
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:9px 2px;border-bottom:1px solid var(--border)">
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
            ${esc(l.surname)}, ${esc(l.first_name)}
          </div>
          <div style="font-size:12px;color:var(--text-muted)">${esc(l.national_id)}</div>
        </div>
        <span class="badge ${badgeClass}">${badgeText}</span>
        ${canChange('learners') ? `<button class="comp-edit-btn" data-uuid="${esc(l.student_uuid)}"
          style="flex-shrink:0;padding:5px 12px;font-size:12px;font-weight:500;
                 background:var(--orange);color:#fff;border:none;border-radius:6px;cursor:pointer">
          Edit
        </button>` : ''}
      </div>`;
  }).join('');
}

function wireLearners(all, fld) {
  document.getElementById('comp-search')?.addEventListener('input', e => {
    const q = e.target.value.toLowerCase().trim();
    const filtered = q ? all.filter(l =>
      l.surname?.toLowerCase().includes(q) ||
      l.first_name?.toLowerCase().includes(q) ||
      l.national_id?.includes(q)) : all;
    document.getElementById('comp-rows').innerHTML = learnerRows(filtered, fld);
    const cnt = document.getElementById('comp-count');
    if (cnt) cnt.textContent = `${filtered.length} learner${filtered.length !== 1 ? 's' : ''}`;
    wireEditBtns();
  });
  wireEditBtns();
}

function wireEditBtns() {
  document.querySelectorAll('.comp-edit-btn').forEach(btn =>
    btn.addEventListener('click', e => {
      e.stopPropagation();
      openEditStudentModal({ studentUuid: btn.dataset.uuid, onSave: () => renderCompliance() });
    })
  );
}

/* ═══════════════════════════════ EXCEL REPORT HELPERS ═══ */
const XL_ORG     = 'LondMarx Group (Pty) Ltd';
const XL_IMPL    = 'Implementing Partner: LondMarx Training (Pty) Ltd';
const XL_PROG    = 'UIF LAP SHORT SKILLS – 2025-26 IMPLEMENTATION';
const XL_CONTACT = 'Contact: Ntsikelelo Klaas  /  084 227 9921';

// ARGB colour palette
const XC = {
  navy:    'FF1F3864', navyTxt: 'FF1F3864',
  blue:    'FF2E75B6', ltBlue:  'FFBDD7EE',
  altRow:  'FFDEEBF7', white:   'FFFFFFFF', black: 'FF000000',
  grnBg:   'FFE2EFDA', grnFg:   'FF375623',
  ambBg:   'FFFFEB9C', ambFg:   'FF7F6000',
  redBg:   'FFFFCCCC', redFg:   'FF9C0006',
  siteBg:  'FFD6E4F0',
};

function xFill(argb)            { return { type:'pattern', pattern:'solid', fgColor:{ argb } }; }
function xFont(sz,bold,argb,it) { return { name:'Calibri', size:sz||11, bold:!!bold, color:{ argb:argb||XC.black }, italic:!!it }; }
function xAlign(h,wrap)         { return { horizontal:h||'left', vertical:'middle', wrapText:!!wrap }; }
function xBorder(w,argb)        { const s={style:w||'thin',color:{argb:argb||'FFB0BEC5'}}; return {top:s,left:s,bottom:s,right:s}; }

function xlNow() {
  return new Date().toLocaleDateString('en-ZA', { day:'2-digit', month:'long', year:'numeric' });
}

function xlNoteLines(date) {
  return [
    `1.  "Verified" = verification flag is TRUE for this field.`,
    `2.  "Unverified" = data exists but has not been verified yet.`,
    `3.  "Missing" = no data has been captured for this field.`,
    `4.  Compliance % = learners verified ÷ total learners in class.`,
    `5.  Traffic light: ≥90% good  ·  60–89% needs review  ·  <60% action required.`,
    `6.  Data source: Supabase public.students — Generated ${date}.`,
  ];
}

// Add the 6-row branding header to a worksheet; returns the next empty row number
function xlBrand(ws, title, total, nc) {
  const date = xlNow();
  const rows = [
    { text: XL_ORG,   bg: XC.navy,   font: xFont(14,true,XC.white),  h: 28 },
    { text: XL_IMPL,  bg: XC.blue,   font: xFont(10,false,XC.white), h: 18 },
    { text: XL_PROG,  bg: XC.blue,   font: xFont(10,true,XC.white),  h: 18 },
    { text: title,    bg: XC.ltBlue, font: xFont(13,true,XC.navyTxt),h: 24 },
    { text: `Report Date: ${date}   |   ${filterLabel()}: ${total.toLocaleString()}   |   ${XL_CONTACT}`,
             bg: XC.white, font: xFont(9),  h: 14 },
    { text: '',       bg: XC.white,  font: xFont(9),  h: 6  },
  ];
  rows.forEach(({ text, bg, font, h }) => {
    const row = ws.addRow([text]);
    ws.mergeCells(row.number, 1, row.number, nc);
    const cell = row.getCell(1);
    cell.fill      = xFill(bg);
    cell.font      = font;
    cell.alignment = xAlign('center');
    row.height     = h;
  });
  return ws.rowCount + 1;
}

// Style a header row (bold, coloured bg, white text, bordered)
function xlHdrRow(ws, values, bg, fgArgb, height) {
  const row = ws.addRow(values);
  row.height = height || 16;
  row.eachCell(cell => {
    cell.fill      = xFill(bg);
    cell.font      = xFont(10, true, fgArgb || XC.white);
    cell.alignment = xAlign('center');
    cell.border    = xBorder();
  });
  return row;
}

// Style a section-header spanning all columns (municipality / site / class)
function xlSectionRow(ws, label, bg, fgArgb, nc, indent, height) {
  const row = ws.addRow([(' ').repeat(indent || 0) + label]);
  ws.mergeCells(row.number, 1, row.number, nc);
  const cell = row.getCell(1);
  cell.fill      = xFill(bg);
  cell.font      = xFont(10, true, fgArgb || XC.white);
  cell.alignment = xAlign('left');
  row.height     = height || 15;
  return row;
}

// Colour a single Verified / Unverified / Missing cell
function xlVerifStyle(cell, status) {
  const m = { Verified:[XC.grnBg,XC.grnFg], Unverified:[XC.ambBg,XC.ambFg], Missing:[XC.redBg,XC.redFg] };
  const [bg, fg] = m[status] || [XC.white, XC.black];
  cell.fill  = xFill(bg);
  cell.font  = xFont(9, false, fg);
  cell.alignment = xAlign('center');
}

// Traffic-light colour for compliance % cell
function xlPctStyle(cell, pct) {
  if      (pct >= 90) { cell.fill = xFill(XC.grnBg); cell.font = xFont(10,true,XC.grnFg); }
  else if (pct >= 60) { cell.fill = xFill(XC.ambBg); cell.font = xFont(10,true,XC.ambFg); }
  else                { cell.fill = xFill(XC.redBg); cell.font = xFont(10,true,XC.redFg); }
  cell.alignment = xAlign('center');
}

// Append notes block
function xlNotes(ws, nc) {
  const spacer = ws.addRow([]); spacer.height = 8;
  const hdr = ws.addRow(['NOTES']);
  ws.mergeCells(hdr.number, 1, hdr.number, nc);
  hdr.getCell(1).fill  = xFill(XC.navy);
  hdr.getCell(1).font  = xFont(10, true, XC.white);
  hdr.height = 14;
  xlNoteLines(xlNow()).forEach(note => {
    const r = ws.addRow([note]);
    ws.mergeCells(r.number, 1, r.number, nc);
    r.getCell(1).font = xFont(9);
    r.height = 13;
  });
}

// Trigger browser download from ExcelJS workbook
async function xlSave(wb, filename) {
  const buf  = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

/* ── Summary sheet: all 6 compliance fields ── */
function xlAddFullSummary(wb, allStudents, titleSuffix) {
  const nc = 7;
  const ws = wb.addWorksheet('Summary');
  ws.columns = [38,12,12,12,12,12,14].map(w => ({ width: w }));

  const title = titleSuffix ? `COMPLIANCE SUMMARY — ${titleSuffix.toUpperCase()}` : 'COMPLIANCE SUMMARY REPORT';
  xlBrand(ws, title, allStudents.length, nc);

  xlHdrRow(ws, ['COMPLIANCE AREA','TOTAL','VERIFIED','% VERIFIED','UNVERIFIED','MISSING','COMPLIANCE %'],
           XC.navy, XC.white, 16);

  COMP_FIELDS.forEach((fld, i) => {
    const { total: t, verified: v, unverified: u, noData: nd } = fieldStats(allStudents, fld);
    const pct = t ? Math.round(v / t * 100) : 0;
    const row = ws.addRow([fld.label, t, v, `${pct}%`, u, nd, `${pct}%`]);
    row.height = 15;
    const bg = i % 2 === 0 ? XC.white : XC.altRow;
    row.eachCell((cell, cn) => {
      cell.fill   = xFill(bg);
      cell.font   = xFont(10);
      cell.border = xBorder();
      cell.alignment = cn === 1 ? xAlign('left') : xAlign('center');
    });
    xlPctStyle(row.getCell(7), pct);
  });

  xlNotes(ws, nc);
}

/* ── Summary sheet: single field with municipality/class breakdown ── */
function xlAddFieldSummary(wb, fld, allStudents, breakdownLabel, breakdownData) {
  const nc = 6;
  const ws = wb.addWorksheet('Summary');
  ws.columns = [32,12,12,12,12,14].map(w => ({ width: w }));

  const { total: t, verified: v, unverified: u, noData: nd } = fieldStats(allStudents, fld);
  const pct = t ? Math.round(v / t * 100) : 0;

  xlBrand(ws, `${fld.label.toUpperCase()} COMPLIANCE REPORT`, allStudents.length, nc);

  xlHdrRow(ws, ['METRIC','','COUNT','% OF TOTAL','',''], XC.blue, XC.white, 15);
  ws.mergeCells(ws.rowCount, 1, ws.rowCount, 2);
  ws.mergeCells(ws.rowCount, 5, ws.rowCount, 6);

  [
    ['Total Learners', t,  '100%'],
    ['  Verified',            v,  `${pct}%`],
    ['  Unverified',          u,  `${t ? Math.round(u  / t * 100) : 0}%`],
    ['  Missing',             nd, `${t ? Math.round(nd / t * 100) : 0}%`],
  ].forEach(([label, count, pctStr], i) => {
    const row = ws.addRow([label, '', count, pctStr, '', '']);
    ws.mergeCells(row.number, 1, row.number, 2);
    ws.mergeCells(row.number, 5, row.number, 6);
    row.height = 14;
    const bg = i % 2 === 0 ? XC.white : XC.altRow;
    [1,3,4].forEach(cn => {
      const c = row.getCell(cn);
      c.fill   = xFill(bg); c.font = xFont(10); c.border = xBorder();
      c.alignment = cn === 1 ? xAlign('left') : xAlign('center');
    });
  });

  if (breakdownData?.length) {
    ws.addRow([]).height = 6;
    xlHdrRow(ws, [breakdownLabel,'TOTAL','VERIFIED','UNVERIFIED','MISSING','COMPLIANCE %'],
             XC.blue, XC.white, 15);
    breakdownData.forEach(({ name, students: ss }, i) => {
      const { total: bt, verified: bv, unverified: bu, noData: bnd } = fieldStats(ss, fld);
      const bpct = bt ? Math.round(bv / bt * 100) : 0;
      const row  = ws.addRow([name, bt, bv, bu, bnd, `${bpct}%`]);
      row.height = 14;
      const bg   = i % 2 === 0 ? XC.white : XC.altRow;
      row.eachCell((cell, cn) => {
        cell.fill   = xFill(bg); cell.font = xFont(10); cell.border = xBorder();
        cell.alignment = cn === 1 ? xAlign('left') : xAlign('center');
      });
      xlPctStyle(row.getCell(6), bpct);
    });
  }

  xlNotes(ws, nc);
}

/* ── Shared: write learner data rows grouped by muni → site → class ── */
function xlWriteAdminRows(ws, nc, counter, verifColFn, muniListOverride) {
  let n = counter;
  for (const muni of (muniListOverride || _muniList)) {
    const venues = [...(_muniVenueIds.get(muni.id) || [])]
      .map(id => _venueMap.get(id)).filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
    if (!venues.length) continue;

    xlSectionRow(ws, muni.name.toUpperCase(), XC.navy,   XC.white,   nc, 0, 16);

    venues.forEach(venue => {
      xlSectionRow(ws, venue.name, XC.blue,   XC.white,   nc, 1, 14);

      const classes = [...venue.classes].sort((a, b) => a.name.localeCompare(b.name));
      classes.forEach(cls => {
        xlSectionRow(ws, cls.name, XC.ltBlue, XC.navyTxt, nc, 2, 13);

        [...applyStatusFilter(cls.students)]
          .sort((a, b) => (a.surname||'').localeCompare(b.surname||''))
          .forEach((s, si) => {
            const bg  = si % 2 === 0 ? XC.white : XC.altRow;
            const row = ws.addRow(verifColFn(n++, s, cls, venue, muni));
            row.height = 13;
            row.eachCell(cell => {
              if (!cell.fill || cell.fill.type !== 'pattern') cell.fill = xFill(bg);
              cell.font   = cell.font?.bold ? cell.font : xFont(9);
              cell.border = xBorder();
            });
          });
      });

      ws.addRow([]).height = 5; // blank separator after each site
    });
  }
}

/* ═══════════════════════════════ EXCEL — ADMIN FULL ═══ */
async function downloadExcel(muniId = null) {
  const muniFilter = muniId ? _muniList.filter(m => m.id === muniId) : _muniList;
  const muniName   = muniId ? (muniFilter[0]?.name || '') : null;

  const seen = new Set(), allStudents = [];
  for (const m of muniFilter) {
    for (const vid of (_muniVenueIds.get(m.id) || [])) {
      const v = _venueMap.get(vid);
      if (!v) continue;
      for (const c of v.classes)
        for (const s of applyStatusFilter(c.students))
          if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }
    }
  }

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();

  xlAddFullSummary(wb, allStudents, muniName);

  const reportTitle = muniName ? `COMPLIANCE DATA — ${muniName.toUpperCase()}` : 'COMPLIANCE DATA REPORT';
  const colHdrs = ['No.','Class','Surname','First Name','ID Number','Status',
    'Banking','Bank Name','Branch Code','Banking Verified','UIF Ref No.','UIF Verified','ID Verified',
    'Tax Ref No.','Tax Verified','Contract','Contract Verified',
    'Highest Qualification','Qual. Verified','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet('Compliance Data');
  ws.columns = [6,28,20,20,16,10,10,18,12,14,16,12,12,16,12,10,14,24,14,36].map(w => ({ width: w }));

  xlBrand(ws, reportTitle, allStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  xlWriteAdminRows(ws, nc, 1, (n, s, cls) => {
    const hasBank = !!s.bank_account_number?.trim();
    const hasCon  = !!s.contract_file_url?.trim();
    const hasQual = !!s.highest_qualification?.trim();
    return [
      n, cls.name, s.surname, s.first_name, s.national_id, s.status,
      hasBank ? 'Yes':'No', s.bank_name||'', s.branch_code||'', hasBank ? (s.bank_verified ?'Verified':'Unverified'):'Missing',
      s.uif_ref_number||'',  s.uif_ref_number?(s.uif_ref_verified ?'Verified':'Unverified'):'Missing',
      s.national_id_verified ?'Verified':'Unverified',
      s.tax_ref_number||'',  s.tax_ref_number?(s.tax_ref_verified  ?'Verified':'Unverified'):'Missing',
      hasCon  ? 'Yes':'No',  hasCon  ? (s.contract_verified      ?'Verified':'Unverified'):'Missing',
      s.highest_qualification||'', hasQual?(s.qualification_verified?'Verified':'Unverified'):'Missing',
      s.payroll_remarks||'',
    ];
  }, muniFilter);

  // Colour verification columns (10,12,13,15,17,19)
  ws.eachRow((row, rn) => {
    if (rn <= 7) return;
    [10,12,13,15,17,19].forEach(cn => {
      const cell = row.getCell(cn);
      if (cell.value) xlVerifStyle(cell, cell.value);
    });
  });

  const safe = s => s.replace(/[^a-zA-Z0-9]+/g, '_');
  const suffix = muniName ? `_${safe(muniName)}` : '';
  await xlSave(wb, `Learner_Compliance${suffix}${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

/* ═══════════════════════════════ MUNICIPALITY PICKER MODAL ═══ */
function pickMuniModal() {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:9999;' +
      'display:flex;align-items:center;justify-content:center';

    overlay.innerHTML = `
      <div style="background:var(--surface);border:1px solid var(--border);border-radius:12px;
                  padding:24px;width:380px;max-width:92vw;box-shadow:0 8px 32px rgba(0,0,0,.22)">
        <h3 style="margin:0 0 6px;font-size:16px;font-weight:700;color:var(--text)">
          Export Compliance Data
        </h3>
        <p style="margin:0 0 16px;font-size:13px;color:var(--text-muted)">
          Select a municipality to export, or export all.
        </p>
        <select id="muni-pick-sel"
          style="width:100%;padding:8px 10px;border:1px solid var(--border);border-radius:8px;
                 font-size:13px;background:var(--surface);color:var(--text);outline:none;
                 margin-bottom:20px">
          <option value="">All Municipalities</option>
          ${_muniList.map(m => `<option value="${esc(m.id)}">${esc(m.name)}</option>`).join('')}
        </select>
        <div style="display:flex;justify-content:flex-end;gap:10px">
          <button id="muni-pick-cancel"
            style="padding:7px 18px;font-size:13px;font-weight:500;border:1px solid var(--border);
                   border-radius:8px;background:var(--surface);color:var(--text);cursor:pointer">
            Cancel
          </button>
          <button id="muni-pick-ok"
            style="padding:7px 18px;font-size:13px;font-weight:500;border:none;border-radius:8px;
                   background:var(--orange);color:#fff;cursor:pointer">
            Download
          </button>
        </div>
      </div>`;

    document.body.appendChild(overlay);
    const cleanup = result => { overlay.remove(); resolve(result); };

    overlay.querySelector('#muni-pick-ok').addEventListener('click', () => {
      const val = overlay.querySelector('#muni-pick-sel').value;
      cleanup(val || null);
    });
    overlay.querySelector('#muni-pick-cancel').addEventListener('click', () => cleanup(undefined));
    overlay.addEventListener('click', e => { if (e.target === overlay) cleanup(undefined); });
  });
}

/* ═══════════════════════════════ EXCEL — FACILITATOR FULL ═══ */
async function downloadExcelFacilitator() {
  const seen = new Set(), allStudents = [];
  for (const cls of _facClasses)
    for (const s of applyStatusFilter(cls.students))
      if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();

  xlAddFullSummary(wb, allStudents);

  const colHdrs = ['No.','Class','Training Venue / Site','Surname','First Name','ID Number','Status',
    'Banking','Bank Name','Branch Code','Banking Verified','UIF Ref No.','UIF Verified','ID Verified',
    'Tax Ref No.','Tax Verified','Contract','Contract Verified',
    'Highest Qualification','Qual. Verified','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet('Compliance Data');
  ws.columns = [6,32,28,20,20,16,10,10,18,12,14,16,12,12,16,12,10,14,24,14,36].map(w => ({ width: w }));

  xlBrand(ws, 'COMPLIANCE DATA REPORT', allStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  let n = 1;
  _facClasses.forEach((cls, ci) => {
    xlSectionRow(ws, cls.class_name, XC.blue, XC.white, nc, 0, 14);
    [...applyStatusFilter(cls.students)].forEach((s, si) => {
      const hasBank = !!s.bank_account_number?.trim();
      const hasCon  = !!s.contract_file_url?.trim();
      const hasQual = !!s.highest_qualification?.trim();
      const bg = si % 2 === 0 ? XC.white : XC.altRow;
      const row = ws.addRow([
        n++, cls.class_name, cls.venue_name, s.surname, s.first_name, s.national_id, s.status,
        hasBank ?'Yes':'No', s.bank_name||'', s.branch_code||'', hasBank?(s.bank_verified ?'Verified':'Unverified'):'Missing',
        s.uif_ref_number||'', s.uif_ref_number?(s.uif_ref_verified?'Verified':'Unverified'):'Missing',
        s.national_id_verified?'Verified':'Unverified',
        s.tax_ref_number||'', s.tax_ref_number?(s.tax_ref_verified?'Verified':'Unverified'):'Missing',
        hasCon ?'Yes':'No',   hasCon ?(s.contract_verified     ?'Verified':'Unverified'):'Missing',
        s.highest_qualification||'', hasQual?(s.qualification_verified?'Verified':'Unverified'):'Missing',
        s.payroll_remarks||'',
      ]);
      row.height = 13;
      row.eachCell(cell => { cell.fill = xFill(bg); cell.font = xFont(9); cell.border = xBorder(); });
      [11,13,14,16,18,20].forEach(cn => {
        const cell = row.getCell(cn);
        if (cell.value) xlVerifStyle(cell, cell.value);
      });
    });
    ws.addRow([]).height = 5;
  });

  await xlSave(wb, `My_Compliance${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

/* ═══════════════════════════════ EXCEL — FIELD (ADMIN) ═══ */
async function downloadFieldAdmin(fld) {
  const seen = new Set(), allStudents = [];
  for (const v of _venueMap.values())
    for (const c of v.classes)
      for (const s of applyStatusFilter(c.students))
        if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const muniBreakdown = _muniList.map(m => {
    const students = [];
    for (const vid of (_muniVenueIds.get(m.id) || [])) {
      const v = _venueMap.get(vid);
      if (v) for (const c of v.classes) students.push(...applyStatusFilter(c.students));
    }
    return { name: m.name, students };
  });

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();

  xlAddFieldSummary(wb, fld, allStudents, 'BY MUNICIPALITY', muniBreakdown);

  const isBanking = fld.key === 'banking';
  const colHdrs = ['No.','Class','Surname','First Name','ID Number','Status', fld.label,
    ...(isBanking ? ['Bank Name','Branch Code'] : []), 'Verification','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet(fld.label.slice(0, 31));
  ws.columns = (isBanking ? [6,28,20,20,16,10,30,18,12,14,36] : [6,28,20,20,16,10,30,14,36]).map(w => ({ width: w }));

  xlBrand(ws, `${fld.label.toUpperCase()} — LEARNER LIST`, allStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  xlWriteAdminRows(ws, nc, 1, (n, s, cls) => {
    const rawVal = String(s[fld.dataField] ?? '').trim();
    const hasVal = !!rawVal;
    return [n, cls.name, s.surname, s.first_name, s.national_id, s.status,
            hasVal ? rawVal : '',
            ...(isBanking ? [s.bank_name||'', s.branch_code||''] : []),
            hasVal ? (s[fld.verField] ? 'Verified' : 'Unverified') : 'Missing',
            s.payroll_remarks||''];
  });

  const verifCol = isBanking ? 10 : 8;
  ws.eachRow((row, rn) => {
    if (rn <= 7) return;
    const cell = row.getCell(verifCol);
    if (cell.value) xlVerifStyle(cell, cell.value);
  });

  await xlSave(wb, `${fld.label.replace(/[^a-zA-Z0-9]+/g,'_')}${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

/* ═══════════════════════════════ EXCEL — FIELD (FACILITATOR) ═══ */
async function downloadFieldFacilitator(fld) {
  const seen = new Set(), allStudents = [];
  for (const cls of _facClasses)
    for (const s of applyStatusFilter(cls.students))
      if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const classBreakdown = _facClasses.map(cls => ({ name: cls.class_name, students: applyStatusFilter(cls.students) }));

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();

  xlAddFieldSummary(wb, fld, allStudents, 'BY CLASS', classBreakdown);

  const isBanking = fld.key === 'banking';
  const colHdrs = ['No.','Class','Training Venue / Site','Surname','First Name','ID Number','Status',
    fld.label, ...(isBanking ? ['Bank Name','Branch Code'] : []), 'Verification','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet(fld.label.slice(0, 31));
  ws.columns = (isBanking ? [6,32,28,20,20,16,10,30,18,12,14,36] : [6,32,28,20,20,16,10,30,14,36]).map(w => ({ width: w }));

  xlBrand(ws, `${fld.label.toUpperCase()} — LEARNER LIST`, allStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  const verifCol = isBanking ? 11 : 9;
  let n = 1;
  _facClasses.forEach(cls => {
    xlSectionRow(ws, cls.class_name, XC.blue, XC.white, nc, 0, 14);
    [...applyStatusFilter(cls.students)].forEach((s, si) => {
      const rawVal = String(s[fld.dataField] ?? '').trim();
      const hasVal = !!rawVal;
      const bg     = si % 2 === 0 ? XC.white : XC.altRow;
      const status = hasVal ? (s[fld.verField] ? 'Verified' : 'Unverified') : 'Missing';
      const row    = ws.addRow([n++, cls.class_name, cls.venue_name,
                                s.surname, s.first_name, s.national_id, s.status,
                                hasVal ? rawVal : '', ...(isBanking ? [s.bank_name||'', s.branch_code||''] : []),
                                status, s.payroll_remarks||'']);
      row.height = 13;
      row.eachCell(cell => { cell.fill = xFill(bg); cell.font = xFont(9); cell.border = xBorder(); });
      xlVerifStyle(row.getCell(verifCol), status);
    });
    ws.addRow([]).height = 5;
  });

  await xlSave(wb, `My_${fld.label.replace(/[^a-zA-Z0-9]+/g,'_')}${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

/* ═══════════════════════════════ EXCEL — MUNICIPALITY ═══ */
async function downloadFieldMuni(fld, muniId, muniName) {
  const venues = [...(_muniVenueIds.get(muniId) || [])]
    .map(id => _venueMap.get(id)).filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  const seen = new Set(), allStudents = [];
  for (const v of venues)
    for (const c of v.classes)
      for (const s of applyStatusFilter(c.students))
        if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const venueBreakdown = venues.map(v => {
    const vs = new Set(), vstu = [];
    for (const c of v.classes)
      for (const s of applyStatusFilter(c.students))
        if (!vs.has(s.student_uuid)) { vs.add(s.student_uuid); vstu.push(s); }
    return { name: v.name, students: vstu };
  });

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();
  xlAddFieldSummary(wb, fld, allStudents, 'BY TRAINING SITE', venueBreakdown);

  const isBanking = fld.key === 'banking';
  const colHdrs = ['No.','Class','Surname','First Name','ID Number','Status',
    fld.label, ...(isBanking ? ['Bank Name','Branch Code'] : []), 'Verification','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet(fld.label.slice(0, 31));
  ws.columns = (isBanking ? [6,28,20,20,16,10,30,18,12,14,36] : [6,28,20,20,16,10,30,14,36]).map(w => ({ width: w }));
  xlBrand(ws, `${fld.label.toUpperCase()} — ${muniName.toUpperCase()}`, allStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  const verifCol = isBanking ? 10 : 8;
  let n = 1;
  for (const v of venues) {
    xlSectionRow(ws, v.name, XC.blue, XC.white, nc, 0, 14);
    const classes = [...v.classes].sort((a, b) => a.name.localeCompare(b.name));
    for (const cls of classes) {
      xlSectionRow(ws, cls.name, XC.ltBlue, XC.navyTxt, nc, 1, 13);
      [...applyStatusFilter(cls.students)].sort((a, b) => (a.surname||'').localeCompare(b.surname||'')).forEach((s, si) => {
        const rawVal = String(s[fld.dataField] ?? '').trim();
        const hasVal = !!rawVal;
        const status = hasVal ? (s[fld.verField] ? 'Verified' : 'Unverified') : 'Missing';
        const bg  = si % 2 === 0 ? XC.white : XC.altRow;
        const row = ws.addRow([n++, cls.name, s.surname, s.first_name, s.national_id, s.status,
          hasVal ? rawVal : '', ...(isBanking ? [s.bank_name||'', s.branch_code||''] : []), status, s.payroll_remarks||'']);
        row.height = 13;
        row.eachCell(cell => { if (!cell.fill || cell.fill.type !== 'pattern') cell.fill = xFill(bg); cell.font = xFont(9); cell.border = xBorder(); });
        xlVerifStyle(row.getCell(verifCol), status);
      });
    }
    ws.addRow([]).height = 5;
  }
  const safe = s => s.replace(/[^a-zA-Z0-9]+/g, '_');
  await xlSave(wb, `${safe(muniName)}_${safe(fld.label)}${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

/* ═══════════════════════════════ EXCEL — VENUE / SITE ═══ */
async function downloadFieldVenue(fld, venue, muniName) {
  const seen = new Set(), allStudents = [];
  for (const c of venue.classes)
    for (const s of applyStatusFilter(c.students))
      if (!seen.has(s.student_uuid)) { seen.add(s.student_uuid); allStudents.push(s); }

  const classBreakdown = venue.classes.map(cls => ({ name: cls.name, students: applyStatusFilter(cls.students) }));

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();
  xlAddFieldSummary(wb, fld, allStudents, 'BY CLASS', classBreakdown);

  const isBanking = fld.key === 'banking';
  const colHdrs = ['No.','Class','Surname','First Name','ID Number','Status',
    fld.label, ...(isBanking ? ['Bank Name','Branch Code'] : []), 'Verification','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet(fld.label.slice(0, 31));
  ws.columns = (isBanking ? [6,28,20,20,16,10,30,18,12,14,36] : [6,28,20,20,16,10,30,14,36]).map(w => ({ width: w }));
  xlBrand(ws, `${fld.label.toUpperCase()} — ${venue.name.toUpperCase()}`, allStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  const verifCol = isBanking ? 10 : 8;
  let n = 1;
  const classes = [...venue.classes].sort((a, b) => a.name.localeCompare(b.name));
  for (const cls of classes) {
    xlSectionRow(ws, cls.name, XC.ltBlue, XC.navyTxt, nc, 0, 13);
    [...applyStatusFilter(cls.students)].sort((a, b) => (a.surname||'').localeCompare(b.surname||'')).forEach((s, si) => {
      const rawVal = String(s[fld.dataField] ?? '').trim();
      const hasVal = !!rawVal;
      const status = hasVal ? (s[fld.verField] ? 'Verified' : 'Unverified') : 'Missing';
      const bg  = si % 2 === 0 ? XC.white : XC.altRow;
      const row = ws.addRow([n++, cls.name, s.surname, s.first_name, s.national_id, s.status,
        hasVal ? rawVal : '', ...(isBanking ? [s.bank_name||'', s.branch_code||''] : []), status, s.payroll_remarks||'']);
      row.height = 13;
      row.eachCell(cell => { if (!cell.fill || cell.fill.type !== 'pattern') cell.fill = xFill(bg); cell.font = xFont(9); cell.border = xBorder(); });
      xlVerifStyle(row.getCell(verifCol), status);
    });
    ws.addRow([]).height = 5;
  }
  const safe = s => s.replace(/[^a-zA-Z0-9]+/g, '_');
  await xlSave(wb, `${safe(venue.name)}_${safe(fld.label)}${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

/* ═══════════════════════════════ EXCEL — CLASS ═══ */
async function downloadFieldClass(fld, cls, contextName) {
  const filteredStudents = applyStatusFilter(cls.students);
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();
  xlAddFieldSummary(wb, fld, filteredStudents, '', []);

  const isBanking = fld.key === 'banking';
  const colHdrs = ['No.','Surname','First Name','ID Number','Status',
    fld.label, ...(isBanking ? ['Bank Name','Branch Code'] : []), 'Verification','Remarks'];
  const nc = colHdrs.length;
  const ws = wb.addWorksheet(fld.label.slice(0, 31));
  ws.columns = (isBanking ? [6,22,20,16,10,30,18,12,14,36] : [6,22,20,16,10,30,14,36]).map(w => ({ width: w }));
  xlBrand(ws, `${fld.label.toUpperCase()} — ${cls.name.toUpperCase()}`, filteredStudents.length, nc);
  xlHdrRow(ws, colHdrs, XC.navy, XC.white, 18);
  ws.views = [{ state:'frozen', xSplit:0, ySplit: ws.rowCount }];

  const verifCol = isBanking ? 9 : 7;
  [...filteredStudents].sort((a, b) => (a.surname||'').localeCompare(b.surname||'')).forEach((s, si) => {
    const rawVal = String(s[fld.dataField] ?? '').trim();
    const hasVal = !!rawVal;
    const status = hasVal ? (s[fld.verField] ? 'Verified' : 'Unverified') : 'Missing';
    const bg  = si % 2 === 0 ? XC.white : XC.altRow;
    const row = ws.addRow([si + 1, s.surname, s.first_name, s.national_id, s.status,
      hasVal ? rawVal : '', ...(isBanking ? [s.bank_name||'', s.branch_code||''] : []), status, s.payroll_remarks||'']);
    row.height = 13;
    row.eachCell(cell => { if (!cell.fill || cell.fill.type !== 'pattern') cell.fill = xFill(bg); cell.font = xFont(9); cell.border = xBorder(); });
    xlVerifStyle(row.getCell(verifCol), status);
  });
  const safe = s => s.replace(/[^a-zA-Z0-9]+/g, '_');
  await xlSave(wb, `${safe(cls.name)}_${safe(fld.label)}${filterSuffix()}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

function noData(msg = 'No data available.') {
  return `<p style="color:var(--text-muted);font-size:13px;padding:8px 0">${msg}</p>`;
}
